/**
 * order-wizard.js
 * (single file / full output)
 */
(() => {
  console.log('[order-wizard] build:', '2026-01-06 20:56');
  const q = (root, sel) => root.querySelector(sel);
  const qa = (root, sel) => Array.from(root.querySelectorAll(sel));
  const PAGE_SIZE = 8;
  const MOUNT_FLAG = 'owMounted';

  // ============================
  // In-memory state (NO localStorage)
  // ============================
  const wizardState = {
    selectedDesignIds: /** @type {string[]} */ ([]),
    layoutId: '',
    fontId: '',
    lastName: '',
    firstName: '',
    onlySingle: false,
    useOldGlyph: false,
    memo: '',
    className: '',
    agentCode: '',
    schoolCode: '',
    agentName: '',
    schoolName: '',
  };

  const setError = (el, msg) => {
    if (!el) return;
    if (!msg) {
      el.hidden = true;
      el.textContent = '';
      return;
    }
    el.hidden = false;
    el.textContent = msg;
  };

  const setHidden = (el, hidden) => {
    if (!el) return;
    el.hidden = !!hidden;
  };
  const setText = (el, text) => {
    if (!el) return;
    el.textContent = text;
  };

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, (m) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    }[m]));
  }

  function getQueryParam(name) {
    try {
      const u = new URL(location.href);
      return (u.searchParams.get(name) || '').trim();
    } catch {
      return '';
    }
  }

  // ============================
  // Param guard (white guide page)
  // ============================
  function renderWhiteGuidePage({ title, message }) {
    const safeTitle = escapeHtml(title || 'このページは注文専用ページです');
    const safeMsg = escapeHtml(message || '').replace(/\n/g, '<br>');

    // 既存UIを見せない（白ページに差し替え）
    document.documentElement.style.height = '100%';
    document.body.style.height = '100%';
    document.body.style.margin = '0';
    document.body.innerHTML = `
      <div style="
        min-height:100vh;background:#fff;color:#111;
        display:flex;align-items:flex-start;justify-content:center;
        padding:48px 16px;box-sizing:border-box;
        font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,Helvetica,Arial,sans-serif;
      ">
        <div style="max-width:680px;width:100%;">
          <div style="font-size:18px;font-weight:700;line-height:1.4;">${safeTitle}</div>
          <div style="margin-top:16px;font-size:14px;line-height:1.8;">${safeMsg}</div>
        </div>
      </div>
    `;
  }

  function guardRequiredParamsOrShowGuide() {
    // このページは agent / school を必須にする（初期表示で止める）
    const agent = getQueryParam('agent');
    const school = getQueryParam('school');
    if (agent && school) return true;

    renderWhiteGuidePage({
      title: 'このページは注文専用ページです',
      message:
        'このページは、学校ごとに発行された専用URLからのアクセスが必要です。\n\n' +
        'お手元の案内をご確認のうえ、正しいURLから再度アクセスしてください。'
    });
    return false;
  }

  async function initFirebase(root) {
    const raw = root.dataset.firebaseConfig || '';
    let cfg;
    try {
      cfg = JSON.parse(raw);
    } catch {
      throw new Error('firebase_config(JSON) が壊れています');
    }
    if (!cfg.projectId) throw new Error('firebase_config に projectId が必要です');
    if (!window.firebase) throw new Error('firebase sdk の読み込みに失敗しました');

    if (!firebase.apps.length) firebase.initializeApp(cfg);

    // Rules次第で必要。公開readなら失敗しても致命じゃない
    try {
      await firebase.auth().signInAnonymously();
    } catch (e) {
      console.warn('anonymous auth failed (may be ok):', e);
    }

    return firebase.firestore();
  }

  /**
   * Firestore DesignDoc を取り込んで正規化
   * DesignDoc:
   *  { id, name, thumbnail, categoryIds?: string[], seals?: Seal[] }
   */
  async function loadDesignDocs(db, collectionName) {
    const snap = await db.collection(collectionName).get();
    const list = snap.docs.map((doc) => {
      const d = doc.data() || {};
      const id = d.id || doc.id;
      return {
        id,
        name: d.name || '',
        thumbnail: d.thumbnail || '',
        categoryIds: Array.isArray(d.categoryIds) ? d.categoryIds : [],
        seals: Array.isArray(d.seals) ? d.seals : [],
      };
    });

    list.sort((a, b) => String(a.name).localeCompare(String(b.name), 'ja'));
    return list;
  }

  async function loadLayoutDocs(db, collectionName = 'layouts') {
    const snap = await db.collection(collectionName).get();

    const list = snap.docs.map((doc) => {
      const d = doc.data() || {};
      const id = d.id || doc.id;
      return {
        id,
        name: d.name || '',
        text: d.text || '',
        positions: Array.isArray(d.positions) ? d.positions : [],
        code: d.code || '',
        popular: !!d.popular,
        bg: d.bg || '',
        order: typeof d.order === 'number' ? d.order : 0,
      };
    });

    list.sort((a, b) => (a.order - b.order) || String(a.name).localeCompare(String(b.name), 'ja'));
    return list;
  }

  async function loadFontDocs(db, collectionName = 'fonts') {
    const snap = await db.collection(collectionName).get();

    const list = snap.docs.map((doc) => {
      const d = doc.data() || {};

      const cssFamily = d.cssFamily || d.family || '"Noto Sans JP"';
      const fallbacks = Array.isArray(d.fallbacks) && d.fallbacks.length
        ? ', ' + d.fallbacks.join(', ')
        : '';
      const family = `${cssFamily}${fallbacks}`;

      const w = Number(d.previewWeight ?? d.weight ?? 500) || 500;

      return {
        id: d.id || doc.id,
        label: d.label || d.name || '',
        family,
        previewWeight: w,
        badge: d.recommended ? 'recommended' : (d.badge || ''),
        order: typeof d.order === 'number' ? d.order : 9999,
      };
    });

    return list;
  }

  // ----- Storage: gs:// -> https download URL（compat版） -----
  async function resolveGsToHttps(gsUrl) {
    const m = String(gsUrl || '').match(/^gs:\/\/[^/]+\/(.+)$/);
    if (!m) return '';
    const path = m[1];

    // ✅ Shopifyページ側で storage compat が未ロードでも落とさない
    if (!firebase.storage) return '';
    const storage = firebase.storage(); // compat
    const ref = storage.ref(path);
    return await ref.getDownloadURL();
  }

  async function resolveThumb(src) {
    if (!src) return '';
    if (src.startsWith('https://')) return src;
    if (src.startsWith('gs://')) return await resolveGsToHttps(src);
    return src;
  }

  const thumbCache = new Map(); // key: src (gs://... or https...), val: resolved https

  async function resolveThumbCached(src) {
    if (!src) return '';
    if (thumbCache.has(src)) return thumbCache.get(src);
    const url = await resolveThumb(src);
    thumbCache.set(src, url);
    return url;
  }

  // ----- CategoryPills（Vue移植） -----
  function mountCategoryPills({ stepEl, chipCats, getChip, setChip, onChipChange }) {
    const wrap = q(stepEl, '[data-ui="cat-wrap"]');
    const dots = q(stepEl, '[data-ui="cat-dots"]');
    const btnPrev = q(stepEl, '[data-action="cat-prev"]');
    const btnNext = q(stepEl, '[data-action="cat-next"]');

    if (!wrap || !dots || !btnPrev || !btnNext) {
      console.warn('[CategoryPills] required DOM not found');
      return { rerender: () => {} };
    }

    let itemStride = 120;
    let visibleCount = 1;
    let activeStart = 0;
    let isScrolling = false;
    let scrollTimer = null;

    const breakLines = (name) => String(name || '').split(/\\n/);

    const styleFrom = (c) => {
      const p = (c && c.palette) ? c.palette : {};
      const bg = p.bgTo
        ? `linear-gradient(180deg, ${p.bgFrom || '#e8f2f6'} 0%, ${p.bgTo} 100%)`
        : (p.bgFrom || '#e8f2f6');

      return {
        '--border': p.border || '#98a5ad',
        '--text': p.text || '#263238',
        '--bg': bg,
      };
    };

    function applyVars(el, vars) {
      for (const [k, v] of Object.entries(vars)) el.style.setProperty(k, String(v));
    }

    function measureStride() {
      const first = wrap.querySelector('.ow-pill');
      const gap = parseFloat(getComputedStyle(wrap).columnGap || '0') || 12;
      const w = first ? first.offsetWidth : 100;
      itemStride = w + gap;
    }

    function updateEnds() {
      if (isScrolling) return;
      const maxLeft = wrap.scrollWidth - wrap.clientWidth;
      const isAtStart = wrap.scrollLeft <= 1;
      const isAtEnd = wrap.scrollLeft >= maxLeft - 1;

      btnPrev.disabled = isAtStart;
      btnNext.disabled = isAtEnd;

      visibleCount = Math.max(1, Math.round(wrap.clientWidth / itemStride));
      const total = chipCats.length;
      const start = Math.round(wrap.scrollLeft / itemStride);
      activeStart = Math.min(Math.max(0, start), Math.max(0, total - visibleCount));

      // dots active（※ .ow-dot を正）
      const dotEls = dots.querySelectorAll('.ow-dot');
      dotEls.forEach((d, i) => {
        const on = i >= activeStart && i < activeStart + visibleCount;
        d.classList.toggle('active', on);
      });
    }

    function debounceUpdate() {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(updateEnds, 100);
    }

    function goIndex(i) {
      if (isScrolling) return;
      isScrolling = true;
      wrap.scrollTo({ left: i * itemStride, behavior: 'smooth' });
      setTimeout(() => {
        updateEnds();
        isScrolling = false;
      }, 250);
    }

    function scroll(dir) {
      if (isScrolling) return;
      isScrolling = true;
      wrap.scrollBy({ left: dir * itemStride, behavior: 'smooth' });
      setTimeout(() => {
        updateEnds();
        isScrolling = false;
      }, 250);
    }

    function render() {
      wrap.innerHTML = '';
      dots.innerHTML = '';

      const chip = getChip();

      chipCats.forEach((c, i) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'ow-pill ow-pill--chip';
        btn.classList.toggle('active', chip === c.name);
        applyVars(btn, styleFrom(c));

        const lines = breakLines(c.name);
        lines.forEach((line, idx) => {
          if (idx > 0) {
            const br = document.createElement('br');
            br.className = 'ow-brsp';
            btn.appendChild(br);
          }
          btn.appendChild(document.createTextNode(line));
        });

        btn.addEventListener('click', () => {
          setChip(c.name);
          render();
          onChipChange?.();
        });

        wrap.appendChild(btn);

        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'ow-dot';
        dot.addEventListener('click', () => goIndex(i));
        dots.appendChild(dot);
      });

      requestAnimationFrame(() => {
        measureStride();
        updateEnds();
      });
    }

    wrap.addEventListener('scroll', debounceUpdate, { passive: true });
    window.addEventListener('resize', () => {
      measureStride();
      updateEnds();
    });

    btnPrev.addEventListener('click', () => scroll(-1));
    btnNext.addEventListener('click', () => scroll(1));

    render();

    return { rerender: render };
  }

  // ----- categories 取得（Firestore） -----
  async function fetchCategories(db) {
    const snap = await db.collection('categories').orderBy('order').get();
    return snap.docs.map(d => {
      const data = d.data() || {};
      return {
        id: d.id,
        name: data.name || '',
        slug: data.slug || '',
        type: data.type || 'chip',
        order: typeof data.order === 'number' ? data.order : 0,
        palette: data.palette || {}
      };
    });
  }

  // ----- Step1（デザイン選択） -----
  function initStep1(root, allDesigns, db, hooks = {}) {
    const limit = Number(root.dataset.designLimit || '5');
    const step = q(root, '[data-step="design"]');
    if (!step) return;

    const grid = q(step, '[data-ui="design-grid"]');
    const pageEl = q(step, '[data-ui="page"]');
    const totalPagesEl = q(step, '[data-ui="total-pages"]');
    const prevBtn = q(step, '[data-action="prev"]');
    const nextBtn = q(step, '[data-action="next"]');

    const catsLoading = q(step, '[data-ui="cats-loading"]');
    const catsError = q(step, '[data-ui="cats-error"]');

    const emptyNoCat = q(step, '[data-ui="design-empty"]');
    const emptyNoHit = q(step, '[data-ui="design-nohit"]');

    const dock = q(step, '[data-ui="dock"]');
    const dockBody = q(step, '[data-ui="dock-body"]');
    const dockThumbs = q(step, '[data-ui="dock-thumbs"]');
    const dockCount = q(step, '[data-ui="dock-count"]');
    const dockToggleBtn = q(step, '[data-action="dock-toggle"]');
    const dockToggleText = q(step, '[data-ui="dock-toggle-text"]');

    // ✅ ここが移植の肝：Liquid側は #order-wizard 直下にあるので root fallback を持つ
    const dockSpacer = q(step, '[data-ui="dock-spacer"]') || q(root, '[data-ui="dock-spacer"]');

    // “高さを持つ本体” を優先して計測（dockがfixed/ラッパーの場合にズレない）
    const dockMeasureTarget = dockBody || null;

    let dockCollapsed = false;

    let selected = (wizardState.selectedDesignIds || []).filter(Boolean);
    wizardState.selectedDesignIds = selected;

    let page = 1;

    let categories = [];
    let activeChipName = '';

    const totalPages = (arr) => Math.max(1, Math.ceil(arr.length / PAGE_SIZE));

    function limitReached() { return selected.length >= limit; }
    function isSelected(id) { return selected.includes(id); }

    function nameToId(name) {
      const c = categories.find((x) => x.name === name);
      return c ? c.id : '';
    }

    function filteredDesigns() {
      const chipId = nameToId(activeChipName);
      if (!chipId) return [];
      return allDesigns.filter((d) => (d.categoryIds || []).includes(chipId));
    }

    function notifyChanged() {
      wizardState.selectedDesignIds = selected.filter(Boolean);
      hooks?.onChange?.();
    }

    // ============================
    // ✅ Dock spacer: height auto follow (Rakuten移植)
    // ============================
    let dockRO = null;
    let dockMO = null;
    let rafId = 0;

    function updateDockSpacer() {
      if (!dockSpacer) return;

      if (!dock || dock.hidden) {
        dockSpacer.style.height = '0px';
        dockSpacer.style.paddingBottom = '0px';
        return;
      }

      const dockHead = dock.querySelector('[data-ui="dock-head"]');
      const headH = dockHead ? dockHead.getBoundingClientRect().height : 0;

      const bodyHidden = !!dockBody && (dockBody.style.display === 'none' || dockBody.hidden);
      const bodyH = (!bodyHidden && dockBody) ? dockBody.getBoundingClientRect().height : 0;

      // ✅ 開いてる時は head + body（閉じてる時は head のみ）
      const h = Math.ceil(headH + bodyH);

      dockSpacer.style.height = `${h}px`;
      dockSpacer.style.paddingBottom = bodyHidden ? '0px' : 'env(safe-area-inset-bottom)';
    }

    function scheduleDockSpacerUpdate() {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updateDockSpacer);
    }

    function mountDockObserverOnce() {
      if (!dock || !dockSpacer) return;
      if (dockRO) return;

      // ドックの伸縮に追従（画像遅延ロード、折りたたみ、フォント等）
      dockRO = new ResizeObserver(() => scheduleDockSpacerUpdate());
      // 計測対象のDOMが変わっても追従
      // ✅ dock は常に監視（折りたたみ時は dockBody が 0 になりがち）
      dockRO.observe(dock);
      // ✅ 本体（dockBody）があるならそれも監視（展開時の実高さ追従）
      if (dockMeasureTarget) dockRO.observe(dockMeasureTarget);

      // viewport changeでも再計算
      window.addEventListener('resize', scheduleDockSpacerUpdate);

      // ✅ iOS Safari等：アドレスバー伸縮/キーボードでviewportが変わる
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', scheduleDockSpacerUpdate, { passive: true });
        window.visualViewport.addEventListener('scroll', scheduleDockSpacerUpdate, { passive: true });
      }

      // ✅ hidden / class / style 変化でも再計算（モーダル開閉・折りたたみの取りこぼし防止）
      dockMO = new MutationObserver(() => scheduleDockSpacerUpdate());
      dockMO.observe(dock, { attributes: true, attributeFilter: ['hidden', 'class', 'style'] });
      if (dockBody) {
        dockMO.observe(dockBody, { attributes: true, attributeFilter: ['hidden', 'class', 'style'] });
      }
    }

    async function render() {
      if (!grid || !pageEl || !totalPagesEl || !prevBtn || !nextBtn) return;

      const chipId = nameToId(activeChipName);
      const list = filteredDesigns();

      setHidden(emptyNoCat, !!chipId);
      setHidden(emptyNoHit, !(chipId && list.length === 0));

      if (!chipId) {
        grid.innerHTML = '';
        setText(pageEl, '1');
        setText(totalPagesEl, '1');
        prevBtn.disabled = true;
        nextBtn.disabled = true;
        return;
      }

      const tp = totalPages(list);
      if (page > tp) page = tp;

      setText(pageEl, String(page));
      setText(totalPagesEl, String(tp));
      prevBtn.disabled = page <= 1;
      nextBtn.disabled = page >= tp;

      const start = (page - 1) * PAGE_SIZE;
      const end = Math.min(start + PAGE_SIZE, list.length);
      const paged = list.slice(start, end);

      grid.innerHTML = '';

      for (const d of paged) {
        const cell = document.createElement('div');
        cell.className = 'ow-cell';

        const disabled = limitReached() && !isSelected(d.id);
        if (isSelected(d.id)) cell.classList.add('selected');
        if (disabled) cell.classList.add('disabled');

        cell.innerHTML = `
          <span class="ow-check-ui" aria-hidden="true"></span>
          <figure class="ow-thumb-card">
            <img class="ow-thumb" alt="${escapeHtml(d.name || '')}" loading="lazy" decoding="async" referrerpolicy="no-referrer">
          </figure>
        `;

        const img = cell.querySelector('img.ow-thumb');
        (async () => {
          try {
            const url = await resolveThumbCached(d.thumbnail);
            if (url && img) img.src = url;
          } catch (e) {
            console.warn('thumb resolve failed:', d.id, d.thumbnail, e);
          }
        })();

        function toggle(checked) {
          if (disabled) return;
          if (checked) {
            if (!isSelected(d.id)) {
              if (limitReached()) return;
              selected = [...selected, d.id];
            }
          } else {
            selected = selected.filter((v) => v !== d.id);
          }
          notifyChanged();
          render();
          renderDock();
        }

        // ✅ スワイプ直後の click 誤爆ガード（Vue移植）
        cell.addEventListener('click', (e) => {
          if (Date.now() < swipe.suppressClickUntil) {
            e.preventDefault?.();
            e.stopPropagation?.();
            return;
          }
          toggle(!isSelected(d.id));
        });
        grid.appendChild(cell);
      }
    }

    function prev() {
      if (page > 1) {
        page--;
        render();
      }
    }
    function next() {
      const list = filteredDesigns();
      if (page < totalPages(list)) {
        page++;
        render();
      }
    }

    prevBtn?.addEventListener('click', prev);
    nextBtn?.addEventListener('click', next);

    async function renderDock() {
      if (!dock || !dockThumbs || !dockCount) return;

      // observerは初回のみmount
      mountDockObserverOnce();

      const ids = selected.filter(Boolean);
      setText(dockCount, String(ids.length));

      if (ids.length === 0) {
        dock.hidden = true;
        scheduleDockSpacerUpdate();
        return;
      }

      dock.hidden = false;

      if (dockBody) dockBody.style.display = dockCollapsed ? 'none' : 'block';
      if (dockToggleText) dockToggleText.textContent = dockCollapsed ? '▶ 開く' : '× 閉じる';

      dockThumbs.innerHTML = '';
      // ✅ 表示状態を切り替えた直後はレイアウトが落ち着くまで2回叩く（Safari対策）
      scheduleDockSpacerUpdate();
      setTimeout(scheduleDockSpacerUpdate, 0);
      // ✅ フォント/画像/レイアウト確定の遅延を吸収（iOS Safariで効く）
      setTimeout(scheduleDockSpacerUpdate, 50);

      const max = limit;
      for (let i = 0; i < max; i++) {
        const id = ids[i];
        if (!id) {
          const empty = document.createElement('div');
          empty.className = 'ow-dock__thumb';
          empty.setAttribute('aria-hidden', 'true');
          dockThumbs.appendChild(empty);
          continue;
        }

        const d = allDesigns.find(x => x.id === id) || { id, name: '', thumbnail: '' };

        const card = document.createElement('div');
        card.className = 'ow-dock__thumb';

        card.innerHTML = `
          <div class="ow-dock__thumb-header">
            <span class="ow-dock__thumb-order">${i + 1}</span>
            <div class="ow-dock__thumb-actions">
              <button type="button" class="ow-dock__btn ow-dock__btn--remove" data-act="remove">⊖</button>
              <button type="button" class="ow-dock__btn" data-act="left"  ${i === 0 ? 'disabled' : ''}>←</button>
              <button type="button" class="ow-dock__btn" data-act="right" ${i === ids.length - 1 ? 'disabled' : ''}>→</button>
            </div>
          </div>
          <div class="ow-dock__thumb-body">
            <img class="ow-dock__img" alt="${escapeHtml(d.name || '')}" loading="lazy" decoding="async" referrerpolicy="no-referrer">
          </div>
        `;

        const img = card.querySelector('img.ow-dock__img');
        (async () => {
          try {
            const url = await resolveThumb(d.thumbnail);
            if (url && img) img.src = url;
          } catch (e) {
            console.warn('dock thumb resolve failed:', id, e);
          }
        })();

        card.querySelector('[data-act="remove"]')?.addEventListener('click', (e) => {
          e.stopPropagation();
          selected = selected.filter(v => v !== id);
          notifyChanged();
          render();
          renderDock();
        });

        card.querySelector('[data-act="left"]')?.addEventListener('click', (e) => {
          e.stopPropagation();
          const idx = selected.indexOf(id);
          if (idx > 0) {
            selected.splice(idx, 1);
            selected.splice(idx - 1, 0, id);
            notifyChanged();
            render();
            renderDock();
          }
        });

        card.querySelector('[data-act="right"]')?.addEventListener('click', (e) => {
          e.stopPropagation();
          const idx = selected.indexOf(id);
          if (idx >= 0 && idx < selected.length - 1) {
            selected.splice(idx, 1);
            selected.splice(idx + 1, 0, id);
            notifyChanged();
            render();
            renderDock();
          }
        });

        dockThumbs.appendChild(card);
      }

      // ✅ 旧：panel計測1回だけ → ❌
      // ✅ 新：ResizeObserver + RAFで常に追従
      scheduleDockSpacerUpdate();
    }

    dockToggleBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      dockCollapsed = !dockCollapsed;
      renderDock();
    });

    // ===== スワイプ（Vue同等：方向ロック + click誤爆ガード）=====
    const swipe = {
      active: false,
      pointerId: -1,
      startX: 0,
      startY: 0,
      lastX: 0,
      lastY: 0,
      locked: /** @type {"none"|"h"|"v"} */ ("none"),
      swiped: false,
      suppressClickUntil: 0,
    };

    // 閾値（Vueと揃え）
    const LOCK_THRESHOLD_PX  = 8;   // 方向確定
    const SWIPE_THRESHOLD_PX = 45;  // ページ送り
    const VERTICAL_GUARD_PX  = 50;  // 斜め誤爆防止

    const isMobileTouch = (e) => (e.pointerType === 'touch' || e.pointerType === 'pen');

    function onPointerDown(e) {
      if (!isMobileTouch(e)) return;

      swipe.active = true;
      swipe.pointerId = e.pointerId;
      swipe.startX = swipe.lastX = e.clientX;
      swipe.startY = swipe.lastY = e.clientY;
      swipe.locked = "none";
      swipe.swiped = false;

      // move を安定させる（対応ブラウザのみ）
      grid?.setPointerCapture?.(e.pointerId);
    }

    function onPointerMove(e) {
      if (!swipe.active) return;
      if (e.pointerId !== swipe.pointerId) return;
      if (swipe.swiped) return;

      swipe.lastX = e.clientX;
      swipe.lastY = e.clientY;

      const dx = swipe.lastX - swipe.startX;
      const dy = swipe.lastY - swipe.startY;

      // 方向ロック（h/v を早めに確定）
      if (swipe.locked === "none") {
        const adx = Math.abs(dx);
        const ady = Math.abs(dy);
        if (adx < LOCK_THRESHOLD_PX && ady < LOCK_THRESHOLD_PX) return;
        swipe.locked = adx > ady ? "h" : "v";
      }

      // 縦ロックなら縦スクロール優先
      if (swipe.locked === "v") return;

      // 斜めが強い場合は誤爆防止
      if (Math.abs(dy) > VERTICAL_GUARD_PX) {
        swipe.active = false;
        return;
      }

      // 横スワイプ確定
      if (Math.abs(dx) >= SWIPE_THRESHOLD_PX) {
        swipe.swiped = true;

        // dx<0 = 左へスワイプ => 次ページ（Vueと同じ）
        if (dx < 0) next();
        else prev();

        // スワイプ直後の click 誤爆ガード
        swipe.suppressClickUntil = Date.now() + 350;
        swipe.active = false;
      }
    }

    function onPointerUp(e) {
      if (e.pointerId !== swipe.pointerId) return;
      swipe.active = false;
      swipe.locked = "none";
      grid?.releasePointerCapture?.(e.pointerId);
    }

    if (grid) {
      // 縦スクロール許可・横パン抑制（CSS側で効いてても保険）
      try { grid.style.touchAction = 'pan-y'; } catch {}

      grid.addEventListener('pointerdown', onPointerDown);
      grid.addEventListener('pointermove', onPointerMove);
      grid.addEventListener('pointerup', onPointerUp);
      grid.addEventListener('pointercancel', onPointerUp);
    }

    // --- カテゴリ初期化
    (async () => {
      try {
        setHidden(catsLoading, false);
        setHidden(catsError, true);

        categories = await fetchCategories(db);

        if (!activeChipName && categories.length > 0) {
          activeChipName = categories[0].name || '';
        }

        mountCategoryPills({
          stepEl: step,
          chipCats: categories.filter((c) => (c.type ? c.type === 'chip' : true)),
          getChip: () => activeChipName,
          setChip: (v) => { activeChipName = v; page = 1; },
          onChipChange: () => { render(); },
        });

        await render();
        await renderDock();
      } catch (e) {
        console.error('categories fetch failed', e);
        setHidden(catsError, false);
        if (catsError) catsError.textContent = 'カテゴリ取得失敗：' + (e?.message || String(e));

        activeChipName = '';
        await render();
      } finally {
        setHidden(catsLoading, true);
      }
    })();
  }

  // ===== LayoutSelector (ported from Vue) =====
  const ORDER = [
    'extra_large', 'large', 'midium', 'small', 'math_small',
    'square', 'square_small', 'ext_small', 'super_ext_small',
    'math_ohajiki', 'math_money'
  ];

  const palette = {
    extra_large: '#b4dcc4',
    large: '#a6d0f0',
    midium: '#fff196',
    small: '#f6bec7',
    math_small: '#f7c1a4',
    square: '#d4b7c5',
    square_small: '#d6c2b1',
    ext_small: '#ecd7d3',
    super_ext_small: '#aec3cf',
    math_ohajiki: '#f8b72e',
    math_money: '#dbe000',
  };

  function colorOf(size){ return palette[size] || '#cfd8dc'; }

  function labelOf(size){
    switch(size){
      case 'extra_large': return '特大';
      case 'large': return '大';
      case 'midium': return '中';
      case 'small': return '小';
      case 'math_small': return '横小';
      case 'square': return '角';
      case 'square_small': return '角小';
      case 'ext_small': return '極小';
      case 'super_ext_small': return '超極小';
      case 'math_ohajiki': return 'おはじき';
      case 'math_money': return 'おかね';
      default: return size;
    }
  }
  function nl(s){ return String(s || '').replace(/\\n/g, '\n'); }

  function codeOfLayout(l){
    if (l.code) return String(l.code).toUpperCase();
    const src = String(l.name || l.id || '').toUpperCase();
    if (src.includes('算数用')) return 'MATH';
    const m = src.match(/[A-Z]/);
    return m ? m[0] : 'A';
  }

  function orientOf(p){ return (p.width >= p.height) ? 'yoko' : 'tate'; }
  const ORIENTED_SIZES = new Set(['ext_small', 'super_ext_small']);

  function countsMapOriented(l){
    const m = new Map();
    const positions = Array.isArray(l.positions) ? l.positions : [];
    for (const p of positions){
      if (!ORDER.includes(p.size)) continue;
      const size = p.size;
      const orient = ORIENTED_SIZES.has(size) ? orientOf(p) : 'yoko';
      const key = `${size}|${orient}`;
      m.set(key, (m.get(key) || 0) + 1);
    }
    return m;
  }
  function headerLabel(size, orient){
    return labelOf(size) + (orient === 'tate' ? 'たて' : '');
  }
  function shrinkClass(size, orient){
    const text = headerLabel(size, orient);
    return text.length >= 4 ? 'is-wrap' : '';
  }
  function countOfOriented(l, size, orient){
    const m = countsMapOriented(l);
    return m.get(`${size}|${orient}`) || 0;
  }

  function parseLayoutAssets(root){
    try{
      const raw = root.dataset.layoutAssets || '';
      return raw ? JSON.parse(raw) : {};
    }catch{
      return {};
    }
  }
  function boardKeyOfLayout(l){
    const keyRaw = String(l.code || codeOfLayout(l) || 'A').toUpperCase();
    if (keyRaw.includes('MATH')) return 'MATH';
    if (['A','B','C'].includes(keyRaw)) return keyRaw;
    return 'A';
  }

  function initStep2(root, layouts, hooks = {}){
    const step = q(root, '[data-step="layout"]');
    if (!step) return;

    const col = q(step, '[data-ui="layout-col"]');
    const errorEl = q(step, '[data-ui="layouts-error"]');
    if (!col) return;

    const assets = parseLayoutAssets(root);
    const marks = (assets.marks || {});
    const boards = (assets.boards || {});
    const popularSrc = assets.popular || '';

    let selectedId = String(wizardState.layoutId || '');
    if (!selectedId && layouts.length){
      selectedId = layouts[0].id;
      wizardState.layoutId = selectedId;
      hooks?.onChange?.();
    }

    const markSrc = (code) => marks[code] || marks.A || '';
    const boardImg = (l) => {
      const key = boardKeyOfLayout(l);
      return (boards[key] || boards.A || { pc:'', sp:'' });
    };

    function renderBadges(l, mountEl){
      mountEl.innerHTML = '';
      const ORIENTS = ['yoko','tate'];

      for (const sz of ORDER){
        for (const o of ORIENTS){
          const cnt = countOfOriented(l, sz, o);
          if (!cnt) continue;

          const badge = document.createElement('span');
          badge.className = 'ow-layout-badge';
          badge.style.setProperty('--c', colorOf(sz));

          const label = document.createElement('strong');
          label.className = 'ow-layout-badge-label ' + shrinkClass(sz, o);
          label.textContent = headerLabel(sz, o);

          const num = document.createElement('strong');
          num.className = 'ow-layout-badge-num';
          num.textContent = String(cnt);

          badge.appendChild(label);
          badge.appendChild(num);
          mountEl.appendChild(badge);
        }
      }
    }

    function render(){
      col.innerHTML = '';

      if (!layouts.length){
        setError(errorEl, 'レイアウトが0件です（Firestoreのlayoutsを確認してください）');
        return;
      }
      setError(errorEl, '');

      for (const l of layouts){
        const card = document.createElement('label');
        card.className = 'ow-layout-card';
        if (l.bg) card.style.setProperty('--ow-card-bg', l.bg);
        if (selectedId === l.id) card.classList.add('is-selected');

        const head = document.createElement('div');
        head.className = 'ow-layout-head';

        const radio = document.createElement('input');
        radio.type = 'radio';
        radio.className = 'ow-layout-radio';
        radio.name = 'ow-layout-radio';
        radio.value = l.id;
        radio.checked = (selectedId === l.id);

        const radioUi = document.createElement('span');
        radioUi.className = 'ow-layout-radio-ui';
        radioUi.setAttribute('aria-hidden', 'true');

        const markWrap = document.createElement('span');
        markWrap.className = 'ow-layout-mark-wrap';
        const markImg = document.createElement('img');
        markImg.className = 'ow-layout-mark-img';
        markImg.alt = '';
        markImg.setAttribute('aria-hidden', 'true');
        markImg.src = markSrc(codeOfLayout(l));
        markWrap.appendChild(markImg);

        const badgeInline = document.createElement('div');
        badgeInline.className = 'ow-layout-badge-inline';
        renderBadges(l, badgeInline);

        const infoInline = document.createElement('div');
        infoInline.className = 'ow-layout-info-inline';
        const descInline = document.createElement('div');
        descInline.className = 'ow-layout-desc-inline';
        descInline.textContent = nl(l.text);
        infoInline.appendChild(descInline);

        head.appendChild(radio);
        head.appendChild(radioUi);
        head.appendChild(markWrap);
        head.appendChild(badgeInline);
        head.appendChild(infoInline);

        const badgeGrid = document.createElement('div');
        badgeGrid.className = 'ow-layout-badge-grid';
        renderBadges(l, badgeGrid);

        const boardWrap = document.createElement('div');
        boardWrap.className = 'ow-layout-board-wrap';

        const board = document.createElement('div');
        board.className = 'ow-layout-board';

        const pic = document.createElement('picture');
        const src = document.createElement('source');
        src.media = '(max-width:768px)';
        src.srcset = boardImg(l).sp || '';
        const img = document.createElement('img');
        img.className = 'ow-layout-board-img';
        img.alt = '';
        img.src = boardImg(l).pc || '';
        pic.appendChild(src);
        pic.appendChild(img);
        board.appendChild(pic);

        if (l.popular && popularSrc){
          const pop = document.createElement('img');
          pop.className = 'ow-layout-popular';
          pop.src = popularSrc;
          pop.alt = '大人気';
          boardWrap.appendChild(pop);
        }

        boardWrap.appendChild(board);

        const info = document.createElement('div');
        info.className = 'ow-layout-info';
        const desc = document.createElement('div');
        desc.className = 'ow-layout-desc';
        desc.textContent = nl(l.text);
        info.appendChild(desc);

        card.appendChild(head);
        card.appendChild(badgeGrid);
        card.appendChild(boardWrap);
        card.appendChild(info);

        const selectThis = () => {
          selectedId = l.id;
          wizardState.layoutId = selectedId;
          hooks?.onChange?.();
          render();
        };
        card.addEventListener('click', selectThis);
        radio.addEventListener('change', selectThis);

        col.appendChild(card);
      }
    }

    render();
  }

  function initStep3(root, fonts, state, hooks = {}){
    const step = q(root, '[data-step="font"]');
    if (!step) return;

    const grid = q(step, '[data-ui="font-grid"]');
    const errorEl = q(step, '[data-ui="fonts-error"]');
    if (!grid) return;

    let selectedId = String(wizardState.fontId || '');
    if (!selectedId && fonts.length){
      selectedId = fonts[0].id;
      wizardState.fontId = selectedId;
      hooks?.onChange?.();
    }

    function applyFontStyle(el, f){
      if (!el) return;
      const w = Number(f.previewWeight || 500);
      el.style.fontFamily = String(f.family || '"Noto Sans JP", sans-serif');
      el.style.fontWeight = String(w);
    }

    function render(){
      if (!fonts.length){
        setError(errorEl, 'フォントが0件です（Firestoreのfontsを確認してください）');
        return;
      }
      setError(errorEl, '');
      grid.innerHTML = '';

      for (const f of fonts){
        const card = document.createElement('label');
        card.className = 'card';
        if (selectedId === f.id) card.classList.add('selected');

        card.innerHTML = `
          <input class="radio" type="radio" name="ow-font" value="${escapeHtml(f.id)}" ${selectedId===f.id?'checked':''} />
          <div class="font-name">
            <span class="dot" aria-hidden="true"></span>
            <span class="label"></span>
          </div>

          <div class="sample">
            <p class="line lg"></p>
            <p class="line sm"></p>
          </div>

          ${f.badge === 'recommended'
            ? `<span class="rec-badge"><span class="rec-badge-main">旧字</span><span class="rec-badge-sub">対応</span></span>`
            : ``}
        `;

        card.querySelector('.font-name .label').textContent = f.label || '';
        card.querySelector('.sample .lg').textContent = '防水お名前シール';
        card.querySelector('.sample .sm').textContent = 'よしだひろと';

        applyFontStyle(card.querySelector('.font-name .label'), f);
        applyFontStyle(card.querySelector('.sample .lg'), f);
        applyFontStyle(card.querySelector('.sample .sm'), f);

        const selectThis = () => {
          selectedId = f.id;
          wizardState.fontId = selectedId;
          hooks?.onChange?.();
          render();
          state?.applyNameFont?.();
        };

        card.addEventListener('click', selectThis);
        card.querySelector('input.radio')?.addEventListener('change', selectThis);

        grid.appendChild(card);
      }
    }

    render();
  }

  function initStepName(root, state, hooks = {}){
    const step = q(root, '[data-step="name"]');
    if (!step) return;

    const elLast  = q(step, '[data-ui="name-last"]');
    const elFirst = q(step, '[data-ui="name-first"]');
    const elOnly  = q(step, '[data-ui="only-single"]');
    const elOld   = q(step, '[data-ui="use-oldglyph"]');
    const memoEl  = q(step, '[data-ui="memo"]');
    const classEl = q(step, '[data-ui="class"]');

    const cntLast  = q(step, '[data-ui="count-last"]');
    const cntFirst = q(step, '[data-ui="count-first"]');
    const max1 = q(step, '[data-ui="maxlen"]');
    const max2 = q(step, '[data-ui="maxlen-2"]');

    const chip = q(step, '[data-ui="name-chip"]');
    const errEl = q(step, '[data-ui="name-error"]');

    const MAX_NORMAL = 10;
    const MAX_SINGLE = 20;

    const setErr = (msg) => setError(errEl, msg);

    function getMax(){ return elOnly?.checked ? MAX_SINGLE : MAX_NORMAL; }

    function getSelectedFont(){
      const id = String(wizardState.fontId || '');
      return (state.fonts || []).find(f => f.id === id) || null;
    }

    function applyFontToChip(){
      if (!chip) return;
      const f = getSelectedFont();
      if (!f) return;
      chip.style.fontFamily = String(f.family || '"Noto Sans JP", sans-serif');
      chip.style.fontWeight = String(Number(f.previewWeight || 500) || 500);
    }

    state.applyNameFont = applyFontToChip;

    function sliceToMax(s){
      const m = getMax();
      return String(s || '').slice(0, m);
    }

    function updateCounts(){
      const m = getMax();
      if (max1) max1.textContent = String(m);
      if (max2) max2.textContent = String(m);

      if (cntLast)  cntLast.textContent  = String((elLast?.value || '').length);
      if (cntFirst) cntFirst.textContent = String((elFirst?.value || '').length);
    }

    function updateChip(){
      if (!chip) return;
      const ln = String(elLast?.value || '').trim();
      const fn = String(elFirst?.value || '').trim();
      const full = [ln, fn].filter(Boolean).join(' ');
      chip.textContent = full || '（未入力）';
    }

    function persistToState(){
      wizardState.lastName = String(elLast?.value || '');
      wizardState.firstName = String(elFirst?.value || '');
      wizardState.onlySingle = !!elOnly?.checked;
      wizardState.useOldGlyph = !!elOld?.checked;
      wizardState.memo = String(memoEl?.value || '');
      wizardState.className = String(classEl?.value || '');
      hooks?.onChange?.();
    }

    function syncUI(){
      const m = getMax();
      if (elLast)  elLast.value  = String(elLast.value || '').slice(0, m);
      if (elFirst) elFirst.value = String(elFirst.value || '').slice(0, m);

      updateCounts();
      updateChip();
      applyFontToChip();
      persistToState();
      setErr('');
    }

    // restore from wizardState
    if (elLast)  elLast.value = String(wizardState.lastName || '');
    if (elFirst) elFirst.value = String(wizardState.firstName || '');
    if (elOnly)  elOnly.checked = !!wizardState.onlySingle;
    if (elOld)   elOld.checked = !!wizardState.useOldGlyph;
    if (memoEl) memoEl.value = String(wizardState.memo || '');
    if (classEl) classEl.value = String(wizardState.className || '');

    syncUI();

    elLast?.addEventListener('input', () => {
      elLast.value = sliceToMax(elLast.value);
      syncUI();
    });
    elFirst?.addEventListener('input', () => {
      elFirst.value = sliceToMax(elFirst.value);
      syncUI();
    });
    elOnly?.addEventListener('change', () => syncUI());
    elOld?.addEventListener('change', () => syncUI());
    memoEl?.addEventListener('input', () => persistToState());
    classEl?.addEventListener('input', () => persistToState());
    classEl?.addEventListener('change', () => persistToState());
  }

  function initPreviewDrawer(root, preview) {
    const overlays = root.querySelectorAll('[data-preview-overlay]');
    const overlay = overlays[overlays.length - 1];
    if (!overlay) return;

    const btnClose = overlay.querySelector('[data-preview-close]');
    const loadingBox = overlay.querySelector('[data-preview-loading]');
    const placeholder = overlay.querySelector('[data-preview-placeholder]');
    const img = overlay.querySelector('[data-preview-img]');
    const trainImg = overlay.querySelector('[data-train-img]');
    const barFill = overlay.querySelector('[data-bar-fill]');
    const percentEl = overlay.querySelector('[data-percent]');

    const frames = (overlay.dataset.trainFrames || '').split(',').map(s => s.trim()).filter(Boolean);
    let frameIndex = 0;
    let frameTimer = null;

    function safeProgress() {
      let p = preview.state.progress ?? 0;
      if (p < 0) p = 0;
      if (p > 100) p = 100;
      return Math.round(p);
    }

    function trainPositionClass(i) {
      if (i === 0) return 'pos-left';
      if (i === 1) return 'pos-center';
      return 'pos-right';
    }

    function startTrainAnim() {
      stopTrainAnim();
      frameTimer = setInterval(() => {
        frameIndex = (frameIndex + 1) % (frames.length || 1);
        render();
      }, 2000);
    }

    function stopTrainAnim() {
      if (frameTimer) {
        clearInterval(frameTimer);
        frameTimer = null;
      }
    }

    function setOpen(open) {
      overlay.hidden = !open;
      root.classList.toggle('is-preview-open', open);
      if (!open) stopTrainAnim();
      render();
    }

    setOpen(false);

    function render() {
      const isOpen = !overlay.hidden;
      if (!isOpen) return;

      const loading = !!preview.state.loading;
      const previewUrl = preview.state.previewUrl || '';

      const animActive = loading && !previewUrl;
      if (animActive) startTrainAnim();
      else stopTrainAnim();

      if (loading && !previewUrl) {
        loadingBox.hidden = false;
        placeholder.hidden = true;
        img.hidden = true;

        const p = safeProgress();
        if (percentEl) percentEl.textContent = `${p}%`;
        if (barFill) barFill.style.width = `${p}%`;

        if (trainImg) {
          if (frames.length) trainImg.src = frames[frameIndex] || frames[0];
          trainImg.classList.remove('pos-left','pos-center','pos-right');
          trainImg.classList.add(trainPositionClass(frameIndex));
        }
      } else if (!previewUrl) {
        loadingBox.hidden = true;
        placeholder.hidden = false;
        img.hidden = true;
      } else {
        loadingBox.hidden = true;
        placeholder.hidden = true;
        img.hidden = false;
        img.src = previewUrl;
      }
    }

    overlay.addEventListener('click', () => setOpen(false));
    btnClose?.addEventListener('click', (e) => { e.stopPropagation(); setOpen(false); });
    overlay.querySelector('.ow-preview-panel')?.addEventListener('click', (e) => e.stopPropagation());

    return { open: () => setOpen(true), close: () => setOpen(false), render };
  }

  function usePreview(ENDPOINT, onTick) {
    const state = {
      previewUrl: '',
      previewMime: '',
      loading: false,
      error: null,
      progress: 0
    };

    let progressTimer = null;

    function clearTimers() {
      if (progressTimer !== null) {
        clearInterval(progressTimer);
        progressTimer = null;
      }
    }

    async function generate(payload) {
      state.loading = true;
      state.error = null;
      state.previewUrl = '';
      state.previewMime = '';
      state.progress = 0;
      clearTimers();
      onTick?.();

      try {
        const fbAuth = firebase.auth();
        if (!fbAuth.currentUser) {
          await fbAuth.signInAnonymously();
        }
        const idToken = await fbAuth.currentUser.getIdToken(true);

        const start = Date.now();
        const duration = 10_000;

        progressTimer = setInterval(() => {
          const elapsed = Date.now() - start;
          if (elapsed >= duration) {
            state.progress = 99;
            clearTimers();
            onTick?.();
            return;
          }
          const ratio = elapsed / duration;
          const target = Math.floor(99 * ratio);
          if (target > state.progress) state.progress = target;
          onTick?.();
        }, 100);

        const res = await fetch(ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`,
          },
          body: JSON.stringify(payload),
        });

        clearTimers();

        if (!res.ok) {
          const text = await res.text().catch(() => '');
          if (res.status === 401) throw new Error('認証エラー：ページを再読み込みしてください');
          if (res.status === 400) throw new Error('入力内容に問題があります：' + (text || '不明なエラー'));
          throw new Error(text || `サーバーエラー (HTTP ${res.status})`);
        }

        const ct = res.headers.get('content-type') || '';
        const buf = await res.arrayBuffer();

        state.progress = 100;
        onTick?.();
        await new Promise(r => setTimeout(r, 300));

        const blob = new Blob([buf], { type: ct });
        state.previewUrl = URL.createObjectURL(blob);
        state.previewMime = ct;
        onTick?.();

        return state.previewUrl;
      } catch (e) {
        clearTimers();
        state.error = e?.message ?? 'プレビューの生成に失敗しました';
        onTick?.();
        throw e;
      } finally {
        state.loading = false;
        onTick?.();
      }
    }

    function reset() {
      clearTimers();
      state.previewUrl = '';
      state.previewMime = '';
      state.error = null;
      state.loading = false;
      state.progress = 0;
      onTick?.();
    }

    return { state, generate, reset };
  }

  // ----- bootstrap -----
  async function boot(root) {
    if (!root) return;
    // ✅ section再描画/複数回呼び出し対策：二重初期化を防ぐ
    if (root.dataset[MOUNT_FLAG] === '1') return;
    root.dataset[MOUNT_FLAG] = '1';

    // ✅ 初期表示ガード：agent/school が無ければ白ページ案内にして以降を止める
    if (!guardRequiredParamsOrShowGuide()) return;
    const collectionName = root.dataset.designsCollection || 'designs';

    try {
      const db = await initFirebase(root);

      const designs = await loadDesignDocs(db, collectionName);

      // =========================================
      // agent/school (readonly) from URL + Firestore
      // =========================================
      wizardState.agentCode = getQueryParam('agent');
      wizardState.schoolCode = getQueryParam('school');

      // ✅ schools docId が環境で揺れる可能性があるので候補を複数試す
      // 例: "001_0001" / "0010001" / "001-0001"
      const agentP = String(wizardState.agentCode || '').trim();
      const schoolP = String(wizardState.schoolCode || '').trim();
      const schoolIdCandidates = (agentP && schoolP)
        ? [
            `${agentP}_${schoolP}`,
            `${agentP}${schoolP}`,
            `${agentP}-${schoolP}`,
          ]
        : [];

      if (schoolIdCandidates.length) {
        console.log('[order-wizard] agent/school:', agentP, schoolP);
        console.log('[order-wizard] schoolIdCandidates:', schoolIdCandidates);

        try {
          for (const schoolId of schoolIdCandidates) {
            const sdoc = await db.collection('schools').doc(String(schoolId)).get();
            if (!sdoc.exists) continue;

            const s = sdoc.data() || {};
            wizardState.schoolName = String(s.name || s.schoolName || '');
            // agentCode はURL優先。schools側が持ってても空のときだけ補完
            if (!wizardState.agentCode) wizardState.agentCode = String(s.agentCode || '');
            // agentName は既に取れてる前提が多いので、空の時だけ補完
            if (!wizardState.agentName) wizardState.agentName = String(s.agentName || '');
            break;
          }
        } catch (e) {
          console.warn('[order-wizard] schools lookup failed:', e);
        }
      }

      // agentName が未取得なら agents/{agentCode}
      if (!wizardState.agentName && wizardState.agentCode) {
        try {
          const adoc = await db.collection('agents').doc(String(wizardState.agentCode)).get();
          if (adoc.exists) {
            const a = adoc.data() || {};
            wizardState.agentName = String(a.name || '');
          }
        } catch (e) {
          console.warn('[order-wizard] agents lookup failed:', e);
        }
      }

      // readonly 表示（DOMが無ければ何もしない）
      const agentSchoolLabelEl = root.querySelector('[data-ui="agent-school-label"]');
      if (agentSchoolLabelEl) {
        const a = wizardState.agentName || wizardState.agentCode || '';
        const s = wizardState.schoolName || wizardState.schoolCode || '';
        agentSchoolLabelEl.textContent = (a && s) ? `${a} / ${s}` : '';
      }

      // checkout btn wiring (needs access inside steps)
      const agreedEl = root.querySelector('[data-ui="agreed"]');
      const addBtn = root.querySelector('[data-action="add-to-cart"]');

      const checkoutErrEl = root.querySelector('[data-ui="checkout-error"]');

      // ===== Validation =====
      function validateName() {
        const ln = String(wizardState.lastName || '').trim();
        const fn = String(wizardState.firstName || '').trim();
        const only = !!wizardState.onlySingle;

        if (!only) {
          if (!ln || !fn) return { ok: false, msg: '姓と名の両方を入力してください' };
          return { ok: true, msg: '' };
        } else {
          const filled = Number(!!ln) + Number(!!fn);
          if (filled === 0) return { ok: false, msg: '姓または名のどちらか一方を入力してください' };
          if (filled === 2) return { ok: false, msg: '姓・名の両方は入力できません（どちらか一方にしてください）' };
          return { ok: true, msg: '' };
        }
      }

      function guardPreview(showMessage = true) {
        if (showMessage) setError(checkoutErrEl, '');

        const hasLayout = !!String(wizardState.layoutId || '');
        const hasDesign = (wizardState.selectedDesignIds || []).filter(Boolean).length > 0;
        const hasFont   = !!String(wizardState.fontId || '');

        if (!hasDesign) {
          if (showMessage) setError(checkoutErrEl, 'デザインを選択してください');
          return false;
        }
        if (!hasLayout) {
          if (showMessage) setError(checkoutErrEl, 'レイアウトを選択してください');
          return false;
        }
        if (!hasFont) {
          if (showMessage) setError(checkoutErrEl, 'フォントを選択してください');
          return false;
        }

        const name = validateName();
        if (!name.ok) {
          const nameErr = root.querySelector('[data-step="name"] [data-ui="name-error"]');
          setError(nameErr, name.msg);
          if (showMessage) setError(checkoutErrEl, name.msg);
          return false;
        }

        return true;
      }

      function guardCheckout(showMessage = true) {
        if (showMessage) setError(checkoutErrEl, '');

        const agent = wizardState.agentCode || getQueryParam('agent');
        const school = wizardState.schoolCode || getQueryParam('school');
        if (!agent || !school) {
          if (showMessage) setError(checkoutErrEl, 'URLに agent / school がありません');
          return false;
        }

        const hasLayout = !!String(wizardState.layoutId || '');
        const hasDesign = (wizardState.selectedDesignIds || []).filter(Boolean).length > 0;
        const hasFont   = !!String(wizardState.fontId || '');
        const agreed    = !!agreedEl?.checked;

        if (!hasDesign) {
          if (showMessage) setError(checkoutErrEl, 'デザインを選択してください');
          return false;
        }
        if (!hasLayout) {
          if (showMessage) setError(checkoutErrEl, 'レイアウトを選択してください');
          return false;
        }
        const name = validateName();
        if (!name.ok) {
          const nameErr = root.querySelector('[data-step="name"] [data-ui="name-error"]');
          setError(nameErr, name.msg);
          if (showMessage) setError(checkoutErrEl, name.msg);
          return false;
        }
        if (!hasFont) {
          if (showMessage) setError(checkoutErrEl, 'フォントを選択してください');
          return false;
        }
        if (!agreed) {
          if (showMessage) setError(checkoutErrEl, '注意事項の確認にチェックを入れてください');
          return false;
        }
        return true;
      }

      function canCheckoutNow() {
        const hasLayout = !!String(wizardState.layoutId || '');
        const hasDesign = (wizardState.selectedDesignIds || []).filter(Boolean).length > 0;
        const hasFont   = !!String(wizardState.fontId || '');
        const agreed    = !!agreedEl?.checked;
        const nameOk = validateName().ok;
        return hasLayout && hasDesign && hasFont && nameOk && agreed;
      }

      function syncCheckoutBtn() {
        if (!addBtn) return;
        addBtn.disabled = !canCheckoutNow();
        if (canCheckoutNow()) setError(checkoutErrEl, '');
      }

      // Step1/2/3/name が state 更新したらボタン反映
      const hooks = { onChange: syncCheckoutBtn };

      initStep1(root, designs, db, hooks);

      const layouts = await loadLayoutDocs(db, 'layouts');
      initStep2(root, layouts, hooks);

      const fonts = await loadFontDocs(db, 'fonts');

      const state = { fonts, layouts };
      initStep3(root, fonts, state, hooks);
      initStepName(root, state, hooks);

      const ENDPOINT = root.dataset.previewEndpoint;
      const preview = { state: { loading:false, progress:0, previewUrl:'' } };

      const drawer = initPreviewDrawer(root, preview);

      const real = usePreview(ENDPOINT, () => drawer?.render?.());
      preview.state = real.state;
      preview.generate = real.generate;
      preview.reset = real.reset;

      root.querySelector('[data-action="open-preview"]')?.addEventListener('click', async () => {
        try {
          if (!guardPreview(true)) return;
          drawer?.open?.();
          preview.state.progress = 0;
          preview.state.loading = true;
          drawer?.render?.();

          const payload = {
            designIds: (wizardState.selectedDesignIds || []).filter(Boolean),
            layoutId: String(wizardState.layoutId || ''),
            familyName: String(wizardState.lastName || ''),
            givenName: String(wizardState.firstName || ''),
            orderId: 'プレビュー',
            fontId: String(wizardState.fontId || '')
          };

          await preview.generate(payload);
          drawer?.render?.();
        } catch (e) {
          drawer?.render?.();
          console.error(e);
        }
      });

      agreedEl?.addEventListener('change', syncCheckoutBtn);
      syncCheckoutBtn();

      async function addToShopifyCart() {
        if (!guardCheckout(true)) return;
        const variantId = Number(root.dataset.mainVariantId || root.dataset.variantId || '0');
        if (!variantId) return;

        const agent = wizardState.agentCode || getQueryParam('agent');
        const school = wizardState.schoolCode || getQueryParam('school');
        const agentName = wizardState.agentName || '';
        const schoolName = wizardState.schoolName || '';

        const designIds = (wizardState.selectedDesignIds || []).filter(Boolean);
        const layoutId = String(wizardState.layoutId || '');
        const fontId = String(wizardState.fontId || '');

        const layoutObj = (state?.layouts || []).find(l => String(l.id) === layoutId);
        const layoutLabel = layoutObj?.name || layoutId;
        const fontObj = (state?.fonts || []).find(f => String(f.id) === fontId);
        const fontLabel = fontObj?.label || fontId; // 見つからなければ従来通りID

        const lastName = String(wizardState.lastName || '').trim();
        const firstName = String(wizardState.firstName || '').trim();
        const onlySingle = !!wizardState.onlySingle;

        const memo = String(wizardState.memo || '');
        const className = String(wizardState.className || '').trim();

        if (!layoutId) return;
        if (!designIds.length) return;
        if (!fontId) return;
        if (!onlySingle && (!lastName || !firstName)) return;

        const properties = {
          '組合コード': agent,
          '学校コード': school,
          '組合名': agentName,
          '学校名': schoolName,
          'クラス': className || '',
          'デザイン': designIds.join('&'),
          'レイアウト': layoutLabel,
          'お名前・姓': lastName,
          'お名前・名': firstName,
          'フォント': fontLabel,
          '備考': memo || '',
        };
        addBtn?.setAttribute('disabled', 'disabled');

        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: [{ id: variantId, quantity: 1, properties }]
            })
          });

          if (!res.ok) {
            const text = await res.text().catch(() => '');
            console.error('cart/add failed:', res.status, text);
            return;
          }

          location.assign('/cart');
        } catch (e) {
          console.error('cart/add error:', e);
        } finally {
          addBtn?.removeAttribute('disabled');
        }
      }
      addBtn?.addEventListener('click', addToShopifyCart);

    } catch (e) {
      console.error(e);
      const step = root.querySelector('[data-step="design"]');
      const err = step?.querySelector('[data-ui="design-error"]');
      if (err) {
        err.hidden = false;
        err.textContent = `デザインの取得に失敗しました：${e.message || e}`;
      }
    }
  }

  function findRootFrom(target) {
    if (!target) return null;
    if (target.id === 'order-wizard') return target;
    return target.querySelector?.('#order-wizard') || null;
  }

  // 初回
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      boot(document.getElementById('order-wizard'));
    });
  } else {
    boot(document.getElementById('order-wizard'));
  }

  // ✅ Shopifyテーマエディタ / section再描画対応
  document.addEventListener('shopify:section:load', (e) => {
    const root = findRootFrom(e.target);
    if (root) boot(root);
  });
  document.addEventListener('shopify:section:reorder', (e) => {
    const root = findRootFrom(e.target);
    if (root) boot(root);
  });
})();